import React, { useState, useEffect } from "react";
import { Box } from "@mui/material";
import { useWindowSize } from "@uidotdev/usehooks";
import { useMapFullscreen } from "../../context/MapFullscreenContext";
import SabiasQue from "../SabiasQue";

// Template133: Día 2 - Namsan Park y N Seoul Tower (con mapa)
// Adaptado del componente PageNamsan.jsx de guia-seul para usar las props del sistema guia
const Template133 = ({
	backgroundColor,
	textColor,
	headerBackgroundColor,
	headerTextColor,
	fontFamily,
	page,
	updatePage,
}) => {
	const size = useWindowSize();
	const isSmallScreen = size.width <= 600;
	const [loaded, setLoaded] = useState(true);
	const [fullscreen, setFullscreen] = useState(false);
	const [isOnline, setIsOnline] = useState(true);
	const { setMapFullscreen } = useMapFullscreen() || {};

	useEffect(() => {
		if (typeof setMapFullscreen === "function") {
			setMapFullscreen(fullscreen);
			return () => setMapFullscreen(false);
		}
	}, [fullscreen, setMapFullscreen]);

	useEffect(() => {
		setIsOnline(navigator.onLine);
		const handleOnline = () => setIsOnline(true);
		const handleOffline = () => setIsOnline(false);
		window.addEventListener("online", handleOnline);
		window.addEventListener("offline", handleOffline);
		return () => {
			window.removeEventListener("online", handleOnline);
			window.removeEventListener("offline", handleOffline);
		};
	}, []);

	const headerText = page?.texts?.[0]?.content;

	const getImageSrc = (id) => {
		if (page?.images && Array.isArray(page.images)) {
			const found = page.images.find((img) => img?.id === id);
			if (found && found.src) return found.src;
		}
		return undefined;
	};

	const getCanvasImageUrl = (id) => {
		if (page?.canvasImages && Array.isArray(page.canvasImages)) {
			const found = page.canvasImages.find((img) => img?.id === id);
			if (found && found.imageUrl) return found.imageUrl;
		}
		return undefined;
	};

	const namsanImage = getImageSrc("namsan");
	const seoulTowerImage = getImageSrc("seoul-tower");
	const towerSticker = getCanvasImageUrl("tower-sticker");
	const logoImage = getCanvasImageUrl("logo");

	const mapUrl = page?.maps?.[0] || "";
	const introText = page?.texts?.[1]?.content;
	const comoSubirTitle = page?.texts?.[2]?.content;
	const comoSubirText = page?.texts?.[3]?.content;
	const namsanTitle = page?.texts?.[4]?.content;
	const namsanDesc = page?.texts?.[5]?.content;
	const towerTitle = page?.texts?.[6]?.content;
	const towerDesc = page?.texts?.[7]?.content;
	const tipAtardecer = page?.texts?.[8]?.content;
	const sabiasQueNamsan = page?.texts?.[9]?.content;
	const sabiasQueTower = page?.texts?.[10]?.content;

	let actualHeaderColor = headerBackgroundColor || "#2E8B80";

	if (fullscreen) {
		return (
			<Box
				sx={{
					position: "fixed",
					top: 0,
					left: 0,
					right: 0,
					bottom: 0,
					backgroundColor: "#fff",
					zIndex: 9999,
					display: "flex",
					flexDirection: "column",
				}}
			>
				<Box
					sx={{
						padding: "10px",
						textAlign: "right",
						backgroundColor: "#2E8B80",
					}}
				>
					<button
						onClick={() => setFullscreen(false)}
						style={{
							backgroundColor: "#fff",
							color: "#2E8B80",
							border: "none",
							borderRadius: "8px",
							padding: "6px 12px",
							fontWeight: 600,
							cursor: "pointer",
							fontFamily: "'Poppins', sans-serif",
						}}
					>
						← Volver a la guía
					</button>
				</Box>
				<iframe
					title="Mapa grande"
					src={mapUrl || "about:blank"}
					width="100%"
					height="100%"
					style={{ border: 0, flex: 1 }}
					allowFullScreen
					loading="lazy"
				></iframe>
			</Box>
		);
	}

	return (
		<Box
			sx={{
				display: "flex",
				flexDirection: "column",
				minHeight: "100%",
				height: "auto",
				width: "100%",
				backgroundColor: backgroundColor || "#f7f7f7",
				position: "relative",
				fontFamily: `${fontFamily || "'Poppins', sans-serif"} !important`,
				"& *": {
					fontFamily: `${fontFamily || "'Poppins', sans-serif"} !important`,
				},
				"& p": {
					marginTop: "0px !important",
					marginBottom: "0px !important",
				},
			}}
		>
			{headerText && (
				<Box
					data-block-id="header"
					data-template-header
					sx={{
						backgroundColor: actualHeaderColor,
						color: headerTextColor || "#fff",
						textAlign: "center",
						padding: "15px 0",
						fontFamily: `${fontFamily || "'Poppins', sans-serif"}`,
						fontSize: "14px",
						fontWeight: 800,
						flexShrink: 0,
						position: "sticky",
						top: 0,
						zIndex: 9999,
					}}
				>
					{headerText}
					{logoImage && !isSmallScreen && (
						<Box
							component="img"
							src={logoImage}
							alt="logo"
							sx={{
								width: 80,
								position: "absolute",
								right: 2,
								top: 18,
								zIndex: 999999,
							}}
						/>
					)}
				</Box>
			)}

			{/* MAPA */}

			<Box data-block-id="map" sx={{ flex: 1, marginTop: "-70px", position: "relative" }}>
				{isOnline && (
					<button
						onClick={() => setFullscreen(true)}
						style={{
							position: "absolute",
							top: 80,
							right: 10,
							backgroundColor: "#2E8B80",
							color: "#fff",
							padding: "8px 10px",
							fontSize: "11px",
							borderRadius: "8px",
							textDecoration: "none",
							fontFamily: "'Poppins', sans-serif",
							boxShadow: "1px 1px 2px rgba(0,0,0,0.2)",

							border: "none",
							cursor: "pointer",
						}}
					>
						Ver mapa grande
					</button>
				)}

				{isOnline && mapUrl ? (
					<>
						<iframe
							title="Mapa"
							src={mapUrl}
							width="100%"
							height="100%"
							allowFullScreen=""
							loading="lazy"
							style={{ border: 0, minHeight: 400 }}
							onLoad={() => setLoaded(true)}
						/>
						{towerSticker && (
							<img
								src={towerSticker}
								alt=""
								style={{
									width: 120,
									position: "absolute",
									bottom: -20,
									right: -20,
								}}
							/>
						)}
						{tipAtardecer && (
							<Box
								sx={{
									position: "absolute",
									bottom: isSmallScreen ? 80 : 80,
									right: isSmallScreen ? 6 : 40,
									backgroundColor: "#fff8db",
									border: "2px solid #f5c400",
									borderRadius: "16px 16px 0px 16px",
									padding: "10px 12px",
									fontSize: 11,
									fontFamily: "'Poppins', sans-serif",
									color: "#444",
									boxShadow: "2px 2px 4px rgba(0,0,0,0.15)",
									maxWidth: 120,
								}}
							>
								{tipAtardecer}
							</Box>
						)}
					</>
				) : !isOnline ? (
					<Box
						sx={{
							width: "100%",
							height: "400px",
							backgroundColor: "#e0e0e0",
							display: "flex",
							alignItems: "center",
							justifyContent: "center",
						}}
					>
						Mapa no disponible offline
					</Box>
				) : null}
			</Box>

			<Box sx={{ padding: "10px 12px", position: "relative" }}>
				{(comoSubirTitle || comoSubirText) && (
					<Box
						data-block-id="text-1"
						sx={{
							backgroundColor: "#caeae6",
							border: "3px solid #2e8b80",
							borderRadius: "12px",
							padding: "12px 12px",
							fontSize: 12,
							fontFamily: "'Poppins', sans-serif",
							color: "#333",
							position: "relative",
							width: "100%",
							marginBottom: "12px",
						}}
					>
						{comoSubirTitle && (
							<Box
								component="h4"
								sx={{
									margin: 0,
									marginBottom: "8px",
									fontSize: 13,
									fontWeight: 700,
									color: "#2e8b80",
									textTransform: "uppercase",
								}}
							>
								{comoSubirTitle}
							</Box>
						)}
						{comoSubirText && (
							<Box
								component="p"
								sx={{ fontFamily: "'Poppins', sans-serif" }}
								dangerouslySetInnerHTML={{ __html: comoSubirText }}
							/>
						)}
					</Box>
				)}

				{(namsanTitle || namsanDesc || namsanImage) && (
					<Box
						data-block-id="text-2"
						sx={{
							flex: 1,
							display: "flex",
							flexDirection: "column",
							gap: "16px",
							position: "relative",
						}}
					>
						<Box
							sx={{ display: "flex", gap: "20px", alignItems: "flex-start" }}
						>
							{namsanImage && (
								<img
									src={namsanImage}
									alt=""
									style={{
										width: "40%",
										minWidth: "40%",
										height: 110,
										borderRadius: 12,
										objectFit: "cover",
									}}
								/>
							)}
							{sabiasQueNamsan && (
								<Box sx={{ position: "absolute", top: 0, left: "33%" }}>
									<SabiasQue icono="🔒" texto={sabiasQueNamsan} />
								</Box>
							)}
							<Box sx={{ width: "60%" }}>
								{namsanTitle && (
									<Box
										component="h3"
										sx={{
											fontFamily: "'Poppins', sans-serif",
											marginBottom: "4px",
											fontSize: 14,
											fontWeight: 700,
											textTransform: "uppercase",
											marginTop: "8px",
										}}
									>
										{namsanTitle}
									</Box>
								)}
								{namsanDesc && (
									<Box
										component="p"
										sx={{
											fontSize: 13,
											color: "#444",
											fontFamily: "'Poppins', sans-serif",
											margin: 0,
										}}
									>
										{namsanDesc}
									</Box>
								)}
							</Box>
						</Box>
					</Box>
				)}

				{(towerTitle || towerDesc || seoulTowerImage) && (
					<Box
						data-block-id="text-3"
						sx={{
							flex: 1,
							display: "flex",
							flexDirection: "column",
							gap: "16px",
							position: "relative",
							marginTop: "16px",
						}}
					>
						<Box
							sx={{ display: "flex", gap: "20px", alignItems: "flex-start" }}
						>
							{seoulTowerImage && (
								<img
									src={seoulTowerImage}
									alt=""
									style={{
										width: "40%",
										minWidth: "40%",
										height: 110,
										borderRadius: 12,
										objectFit: "cover",
									}}
								/>
							)}
							{sabiasQueTower && (
								<Box sx={{ position: "absolute", top: 0, left: "33%" }}>
									<SabiasQue icono="🗼" texto={sabiasQueTower} />
								</Box>
							)}
							<Box sx={{ width: "60%" }}>
								{towerTitle && (
									<Box
										component="h3"
										sx={{
											fontFamily: "'Poppins', sans-serif",
											marginBottom: "4px",
											fontSize: 14,
											fontWeight: 700,
											textTransform: "uppercase",
											marginTop: "8px",
										}}
									>
										{towerTitle}
									</Box>
								)}
								{towerDesc && (
									<Box
										component="p"
										sx={{
											fontSize: 13,
											color: "#444",
											fontFamily: "'Poppins', sans-serif",
											margin: 0,
										}}
									>
										{towerDesc}
									</Box>
								)}
							</Box>
						</Box>
					</Box>
				)}
			</Box>
		</Box>
	);
};

Template133.thumbnail = "/images/template-1.png";

export default Template133;
